import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiCargoIdsConnectPost } from '@/entities/cargo-id';
import { cargoIdConnectSchema, type CargoIdConnectValues } from '@/features/cargo-id/modules/types';

const BACK_TO = '/ids';

export const WidgetsIdsConnectMain = () => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const toast = useToast();
  const form = useForm<CargoIdConnectValues>({
    resolver: zodResolver(cargoIdConnectSchema),
    defaultValues: {
      code: '',
      full_name: '',
      phone_number: '',
    },
    mode: 'onBlur',
  });

  const connect = useMutate({
    api: (values: CargoIdConnectValues) => apiCargoIdsConnectPost(values),
    invalidateKeys: [queryKeys.cargoIds.all],
    onSuccess: () => {
      toast.success(t('connect.toast.connected'));
      navigate(BACK_TO);
    },
    onError: (error, message) => {
      if (error instanceof AxiosError && error.response?.status === 404) {
        form.setError('code', { message: t('connect.errors.notFound') });
        return;
      }
      toast.error(message);
    },
  });

  const err = (key: keyof CargoIdConnectValues): string | undefined => {
    const message = form.formState.errors[key]?.message;
    return message ? t(message) : undefined;
  };

  return (
    <>
      <PageHeader title={t('connect.title')} showBack backTo={BACK_TO} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        <form
          onSubmit={form.handleSubmit((values) => connect.mutate(values))}
          className="flex flex-col gap-3"
          data-cy="cargo-id-connect-form"
        >
          {(() => {
            // Auto-upper-case the Cargo ID code so typing lowercase still
            // produces a canonical, upper-case value submitted to the API.
            const codeReg = form.register('code', {
              setValueAs: (v: unknown) =>
                typeof v === 'string' ? v.toUpperCase() : '',
            });
            return (
              <Input
                label={t('connect.fields.codeLabel')}
                required
                placeholder={t('connect.fields.code')}
                autoCapitalize="characters"
                inputMode="text"
                error={err('code')}
                {...codeReg}
                onChange={(e) => {
                  const upper = e.target.value.toUpperCase();
                  if (e.target.value !== upper) e.target.value = upper;
                  form.setValue('code', upper, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            );
          })()}
          <Input
            label={t('fields.fullName')}
            required
            error={err('full_name')}
            {...form.register('full_name')}
          />
          <Input
            label={t('fields.phone')}
            required
            inputMode="tel"
            placeholder={t('fields.phonePlaceholder')}
            error={err('phone_number')}
            {...form.register('phone_number')}
          />
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(BACK_TO)}
              disabled={connect.isPending}
              block
            >
              {t('common:cancel')}
            </Button>
            <Button type="submit" loading={connect.isPending} block>
              {t('connect.submit')}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
